import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User as UserIcon, ClipboardList, StickyNote, LogOut, ChevronDown,
  ArrowLeft, Plus, Trash2, Camera, Upload,
} from 'lucide-react';
import clsx from 'clsx';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const AVATAR_KEY = 'apex_avatar';
const NOTES_KEY = 'apex_notes_v2';
const NOTES_MIGRATED_KEY = 'apex_usernotes_migrated';

interface Note { id: string; title: string; body: string; updatedAt: number }

function loadLocalNotes(): Note[] {
  try { return JSON.parse(localStorage.getItem(NOTES_KEY) ?? '[]'); } catch { return []; }
}
function loadAvatar(): string | null {
  try { return localStorage.getItem(AVATAR_KEY); } catch { return null; }
}

export function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'menu' | 'perfil'>('menu');
  const [notesDrawerOpen, setNotesDrawerOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // Prioriza avatar do backend (AuthenticatedUser), fallback para localStorage
  const [avatar, setAvatar] = useState<string | null>(user?.avatarUrl || loadAvatar());

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setTab('menu');
      }
    }
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!user) return null;
  const initials = user.nome.split(' ').slice(0, 2).map((p) => p[0]).join('').toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          'flex items-center gap-1.5 px-1 py-0.5 rounded-full transition-colors',
          open ? 'bg-panel-2' : 'hover:bg-panel-2',
        )}
      >
        <Avatar src={avatar} initials={initials} size={28} />
        <ChevronDown className={clsx('w-3 h-3 text-muted transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-80 bg-panel border border-border rounded-lg shadow-xl shadow-black/40 overflow-hidden z-50">
          {tab === 'menu' && (
            <>
              <div className="px-3 py-3 border-b border-border bg-panel-2 flex items-center gap-2.5">
                <Avatar src={avatar} initials={initials} size={36} />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold truncate">{user.nome}</div>
                  <div className="text-[10px] text-muted truncate">{user.email}</div>
                  <div className="text-[10px] text-blue-light mt-0.5">{user.papel}</div>
                </div>
              </div>
              <div className="py-1">
                <MenuItem icon={UserIcon}      label="Perfil"           onClick={() => setTab('perfil')} />
                <MenuItem icon={ClipboardList} label="Minhas demandas"  onClick={() => { setOpen(false); navigate('/minhas-demandas'); }} />
                <MenuItem icon={StickyNote}    label="Notes"            onClick={() => { setOpen(false); setTab('menu'); setNotesDrawerOpen(true); }} />
                <div className="border-t border-border my-1" />
                <MenuItem icon={LogOut}        label="Sair"             onClick={() => logout()} variant="danger" />
              </div>
            </>
          )}

          {tab === 'perfil' && (
            <PerfilPane
              nome={user.nome} email={user.email} papel={user.papel}
              avatar={avatar} setAvatar={setAvatar}
              telefoneInicial={user.telefone}
              onBack={() => setTab('menu')}
            />
          )}
        </div>
      )}
    <NotesDrawer open={notesDrawerOpen} onClose={() => setNotesDrawerOpen(false)} />
    </div>
  );
}

function Avatar({ src, initials, size }: { src: string | null; initials: string; size: number }) {
  if (src) {
    return <img src={src} alt="" style={{ width: size, height: size }} className="rounded-full object-cover" />;
  }
  return (
    <div
      style={{ width: size, height: size }}
      className="rounded-full bg-gradient-to-br from-blue via-purple to-pink flex items-center justify-center text-white font-bold"
    >
      <span style={{ fontSize: size * 0.4 }}>{initials}</span>
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, variant }: {
  icon: typeof UserIcon; label: string; onClick: () => void; variant?: 'danger';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex items-center gap-2.5 w-full px-3 py-2 text-xs hover:bg-panel-2 transition-colors text-left',
        variant === 'danger' ? 'text-red' : 'text-text-2 hover:text-text',
      )}
    >
      <Icon className="w-3.5 h-3.5 opacity-80" />
      {label}
    </button>
  );
}

function PerfilPane({
  nome, email, papel, avatar, setAvatar, onBack, telefoneInicial,
}: {
  nome: string; email: string; papel: string;
  avatar: string | null; setAvatar: (v: string | null) => void;
  onBack: () => void; telefoneInicial?: string;
}) {
  const [telefone, setTelefone] = useState(telefoneInicial ?? '');
  const fileRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<number | null>(null);

  function pickFile() { fileRef.current?.click(); }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 512 * 1024) {
      alert('Limite 500KB pro avatar (base64 cresce o JSON). Comprime antes.');
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = reader.result as string;
      try { localStorage.setItem(AVATAR_KEY, b64); } catch { /* noop */ }
      setAvatar(b64);
      const pessoaId = (window as unknown as { __apexUserId?: string }).__apexUserId;
      if (pessoaId) {
        api.pessoas.update(pessoaId, { avatarUrl: b64 })
          .catch((err) => console.warn('falha sincronizando avatar:', err));
      }
    };
    reader.readAsDataURL(f);
  }

  function removerAvatar() {
    try { localStorage.removeItem(AVATAR_KEY); } catch { /* noop */ }
    setAvatar(null);
    const pessoaId = (window as unknown as { __apexUserId?: string }).__apexUserId;
    if (pessoaId) {
      api.pessoas.update(pessoaId, { avatarUrl: '' })
        .catch((err) => console.warn('falha removendo avatar:', err));
    }
  }

  function handleTelefone(v: string) {
    setTelefone(v);
    const pessoaId = (window as unknown as { __apexUserId?: string }).__apexUserId;
    if (!pessoaId) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      api.pessoas.update(pessoaId, { telefone: v })
        .catch((err) => console.warn('falha sincronizando telefone:', err));
    }, 800) as unknown as number;
  }

  const initials = nome.split(' ').slice(0, 2).map((p) => p[0]).join('').toUpperCase();

  return (
    <div>
      <div className="px-3 py-2 border-b border-border flex items-center justify-between">
        <button type="button" onClick={onBack} className="text-[11px] text-muted hover:text-text inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> voltar
        </button>
        <div className="text-[11px] font-bold uppercase tracking-wider text-text-2">Perfil</div>
      </div>
      <div className="p-3 space-y-3 text-xs">
        <div className="flex items-center gap-3">
          <Avatar src={avatar} initials={initials} size={56} />
          <div className="flex-1 flex flex-col gap-1.5">
            <button type="button" onClick={pickFile} className="btn-secondary !py-1 !px-2 text-[11px]">
              <Camera className="w-3 h-3" /> {avatar ? 'Trocar foto' : 'Subir foto'}
            </button>
            {avatar && (
              <button type="button" onClick={removerAvatar} className="text-[10px] text-muted hover:text-red text-left">
                remover
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
          </div>
        </div>
        <Field label="Nome" value={nome} readOnly />
        <Field label="Email corporativo" value={email} readOnly />
        <Field label="Papel" value={papel} readOnly />
        <div>
          <div className="text-[9px] uppercase tracking-wider text-muted mb-0.5">Telefone</div>
          <input
            className="input w-full text-xs"
            placeholder="+55 27 9..."
            value={telefone}
            onChange={(e) => handleTelefone(e.target.value)}
          />
        </div>
      </div>
      <div className="px-3 py-2 border-t border-border bg-panel-2 text-[10px] text-muted">
        Foto e telefone sincronizados automaticamente com seu perfil.
      </div>
    </div>
  );
}


function Field({ label, value, readOnly }: { label: string; value: string; readOnly?: boolean }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-muted mb-0.5">{label}</div>
      <div className={clsx('text-text', readOnly ? 'opacity-70' : '', 'font-semibold')}>{value}</div>
    </div>
  );
}

// Não exportado mas usado pra type-check do component Camera/Upload (apaga warning)
export const _icons = [Upload];


function NotesDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState<Note[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<Record<string, number>>({});
  const loadedRef = useRef(false);

  // Carrega notas do backend (migra localStorage na primeira vez)
  useEffect(() => {
    if (!open || !user?.email || loadedRef.current) return;
    loadedRef.current = true;
    setLoading(true);

    api.anotacoes.list('geral', `notas:${user.email}`)
      .then(async (res) => {
        let backendNotes: Note[] = res.data.map((a) => {
          // Formato: título na primeira linha, corpo no resto
          const lines = a.texto.split('\n');
          return {
            id: a.id,
            title: lines[0] ?? '',
            body: lines.slice(1).join('\n'),
            updatedAt: new Date(a.editadoEm ?? a.criadoEm).getTime(),
          };
        });

        // Migração one-time de localStorage
        const migrated = localStorage.getItem(NOTES_MIGRATED_KEY);
        if (!migrated) {
          const local = loadLocalNotes();
          if (local.length > 0) {
            const existingTitles = new Set(backendNotes.map((n) => n.title));
            for (const ln of local) {
              if (existingTitles.has(ln.title)) continue;
              try {
                const texto = [ln.title, ln.body].filter(Boolean).join('\n');
                const created = await api.anotacoes.create({
                  texto,
                  recursoTipo: 'geral',
                  recursoId: `notas:${user.email}`,
                });
                backendNotes.push({
                  id: created.data.id,
                  title: ln.title,
                  body: ln.body,
                  updatedAt: ln.updatedAt,
                });
              } catch (err) {
                console.warn('migração nota falhou:', err);
              }
            }
            localStorage.setItem(NOTES_MIGRATED_KEY, '1');
            try { localStorage.removeItem(NOTES_KEY); } catch { /* noop */ }
          } else {
            localStorage.setItem(NOTES_MIGRATED_KEY, '1');
          }
        }

        setNotes(backendNotes.sort((a, b) => b.updatedAt - a.updatedAt));
      })
      .catch((err) => {
        console.warn('falha carregando notas:', err);
        // Fallback para localStorage
        setNotes(loadLocalNotes());
      })
      .finally(() => setLoading(false));
  }, [open, user?.email]);

  function criar() {
    if (!user?.email) return;
    const texto = 'Nova nota';
    api.anotacoes.create({ texto, recursoTipo: 'geral', recursoId: `notas:${user.email}` })
      .then((res) => {
        const n: Note = {
          id: res.data.id,
          title: 'Nova nota',
          body: '',
          updatedAt: Date.now(),
        };
        setNotes((prev) => [n, ...prev]);
        setEditingId(n.id);
      })
      .catch((err) => console.warn('falha criando nota:', err));
  }

  function update(id: string, patch: Partial<Note>) {
    setNotes((prev) => prev.map((n) => (n.id === id ? { ...n, ...patch, updatedAt: Date.now() } : n)));
    // Debounce save para backend
    if (debounceRef.current[id]) window.clearTimeout(debounceRef.current[id]);
    debounceRef.current[id] = window.setTimeout(() => {
      const note = notes.find((n) => n.id === id);
      if (!note) return;
      const merged = { ...note, ...patch };
      const texto = [merged.title, merged.body].filter(Boolean).join('\n');
      api.anotacoes.update(id, texto).catch((err) => console.warn('falha salvando nota:', err));
    }, 600) as unknown as number;
  }

  function remover(id: string) {
    if (!confirm('Apagar essa nota?')) return;
    setNotes((prev) => prev.filter((n) => n.id !== id));
    if (editingId === id) setEditingId(null);
    api.anotacoes.delete(id).catch((err) => console.warn('falha removendo nota:', err));
  }

  const editing = editingId ? notes.find((n) => n.id === editingId) : null;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex" onClick={onClose}>
      <div className="flex-1 bg-black/40 backdrop-blur-[1px]" />
      <aside
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[640px] bg-panel border-l border-border shadow-2xl shadow-black/40 flex flex-col"
      >
        <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-panel-2">
          <div className="flex items-center gap-2">
            <StickyNote className="w-4 h-4 text-yellow" />
            <h2 className="text-sm font-bold">Minhas notas</h2>
            <span className="text-[10px] text-muted">({notes.length})</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={criar}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-blue-light hover:text-blue rounded"
            >
              <Plus className="w-3 h-3" /> nova
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1 text-muted hover:text-text rounded"
              aria-label="fechar"
            >
              <ArrowLeft className="w-4 h-4 rotate-180" />
            </button>
          </div>
        </div>

        <div className="flex-1 grid grid-cols-[220px_1fr] min-h-0">
          {/* Lista lateral */}
          <div className="border-r border-border overflow-y-auto bg-panel-2/40">
            {loading && (
              <div className="p-4 text-[11px] text-muted text-center">Carregando notas...</div>
            )}
            {!loading && notes.length === 0 && (
              <div className="p-4 text-[11px] text-muted text-center">
                Nenhuma nota ainda. Clique em <strong>+ nova</strong>.
              </div>
            )}
            {notes.map((n) => {
              const active = n.id === editingId;
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => setEditingId(n.id)}
                  className={
                    'w-full text-left px-3 py-2 border-b border-border last:border-b-0 transition-colors ' +
                    (active ? 'bg-blue/10 text-text' : 'hover:bg-panel-2 text-text-2')
                  }
                >
                  <div className="text-xs font-semibold truncate">{n.title || '(sem título)'}</div>
                  <div className="text-[10px] text-muted truncate mt-0.5">{n.body || 'vazio…'}</div>
                  <div className="text-[9px] text-muted mt-0.5">
                    {new Date(n.updatedAt).toLocaleString('pt-BR')}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Editor */}
          <div className="flex flex-col min-w-0">
            {editing ? (
              <>
                <div className="px-4 py-2 border-b border-border flex items-center gap-2">
                  <input
                    autoFocus
                    className="input flex-1 text-sm font-semibold"
                    value={editing.title}
                    onChange={(e) => update(editing.id, { title: e.target.value })}
                    placeholder="Título"
                  />
                  <button
                    type="button"
                    onClick={() => remover(editing.id)}
                    className="p-1.5 text-muted hover:text-red rounded"
                    aria-label="apagar"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <textarea
                  value={editing.body}
                  onChange={(e) => update(editing.id, { body: e.target.value })}
                  placeholder="Escreve aqui…"
                  className="flex-1 input rounded-none border-0 text-[13px] leading-relaxed resize-none focus:outline-none px-4 py-3"
                />
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted text-xs">
                Selecione uma nota à esquerda ou crie uma nova.
              </div>
            )}
            <div className="px-4 py-2 border-t border-border bg-panel-2 text-[10px] text-muted">
              Notas sincronizadas automaticamente entre dispositivos.
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
